import {
	type FetchWalletTransferOutCostParams,
	SsuLogoPlaceholder,
	useFormatAmount,
	type WalletTransferOutRequestCostDTO
} from '@capsicum/common';
import { useMainCurrenciesQuery, usePartners, useWalletFormatter } from '@capsicum/socios-core';
import { useCallback } from 'react';

import { useWalletTransferOutCostQuery } from '../data-access';
import { TypeCurrency, type WalletTransferOutCostItem } from '../models';

/**
 * @param params - FetchWalletTransferOutCostParams
 * @returns get transferOutCost data and formatted values to display
 */
export const useWalletTransferOutCost = (params: FetchWalletTransferOutCostParams) => {
	const { formatAmount } = useFormatAmount();
	const { formatPrice } = useWalletFormatter();
	const { data: currencies, isLoading: isLoadingCurrencies } = useMainCurrenciesQuery();
	const { getPartnerBy } = usePartners();
	const { altCurrency } = currencies ?? {};

	const queryResult = useWalletTransferOutCostQuery(
		params,
		useCallback(
			(data?: undefined | WalletTransferOutRequestCostDTO): undefined | WalletTransferOutCostItem => {
				const isCHZWithdraw = data?.currency?.type === TypeCurrency.Crypto;
				const transferOutCostData = {
					currency: data?.currency,
					externallyReceivedAmount: data?.externallyReceivedAmount,
					fromLedgerId: data?.fromLedgerId,
					payment: {
						amount: data?.payment?.amount,
						currency: data?.payment?.currency,
						fee: data?.payment?.fee,
						processStartedAt: data?.payment?.processStartedAt,
						totalAmount: data?.payment?.totalAmount,
						vat: data?.payment?.vat
					},
					withdrawToNetwork: data?.withdrawToNetwork
				};
				let formattedFeeAmount = '';
				let formattedAmountToWithdraw = '';
				const partner = getPartnerBy(p => p.currencyId === params?.currencyId);

				const formattedTotalAmount = `${params.amount} ${data?.currency?.shortCode}`;
				// TODO: change when BE interface is updated
				const preferredAltCurrencyId = altCurrency?.id as unknown as number;
				const convertedAmount = preferredAltCurrencyId
					? data?.payment?.convertedAmounts?.[preferredAltCurrencyId]?.totalAmount
					: '';

				const formattedConvertedPrice = formatPrice({ amount: convertedAmount, currency: altCurrency });

				if (isCHZWithdraw) {
					const feeAmount =
						data?.payment?.fee?.amount && Number(data?.payment?.fee?.amount) > 0
							? Number(data?.payment?.fee?.amount) + Number(data?.payment?.vat?.feeVatAmount)
							: '';

					formattedFeeAmount = `-${formatAmount(feeAmount, {
						maximumFractionDigits: 2,
						minimumFractionDigits: 2
					})} ${data?.payment?.currency?.shortCode}`;

					formattedAmountToWithdraw = `${formatAmount(data?.payment?.amount, {
						maximumFractionDigits: 2,
						minimumFractionDigits: 2
					})} ${data?.payment?.currency?.shortCode}`;
				}

				return {
					formattedAmountToWithdraw,
					formattedConvertedPrice,
					formattedFeeAmount,
					formattedTotalAmount,
					isCHZWithdraw,
					isLoading: isLoadingCurrencies,
					partner: {
						id: partner?.id,
						logo: partner?.images?.partnerLogo?.url || (SsuLogoPlaceholder as unknown as string),
						name: partner?.name ?? ''
					},
					transferOutCostData
				};
			},
			[
				getPartnerBy,
				params.amount,
				params?.currencyId,
				altCurrency,
				formatPrice,
				isLoadingCurrencies,
				formatAmount
			]
		)
	);

	return {
		...queryResult,
		isLoading: queryResult.isLoading || !!queryResult.data?.isLoading
	};
};
